import { Criticidad } from '../value-objects/Criticidad';
import { EstadoAlarma } from '../value-objects/EstadoAlarma';

/**
 * Situación de la condición que originó la alarma, independiente de si alguien
 * la reconoció o resolvió.
 *
 * `presente` = el sensor sigue fuera de rango.
 * `normalizada` = el valor volvió a su sitio y la alarma sólo espera cierre.
 * `desconocida` = el sistema no reporta ese dato.
 */
export type CondicionAlarma = 'presente' | 'normalizada' | 'desconocida';

export interface AlarmaProps {
  id: string;
  sensorId: number | null;
  sensorNombre: string | null;
  sensorUnidad: string | null;
  esDigital: boolean;
  criticidad: Criticidad;
  estado: EstadoAlarma;
  mensaje: string | null;
  observaciones: string | null;
  /** `analogico` o `digital`. */
  tipoUmbral: string | null;
  /** Valor numérico que disparó la alarma (sensores analógicos). */
  valorDisparo: number | null;
  /** Estado que disparó la alarma (sensores digitales). */
  estadoDisparo: boolean | null;
  umbralMinimo: number | null;
  umbralMaximo: number | null;
  estadoEsperado: boolean | null;
  histeresis: number | null;
  /** La alarma se mantiene hasta que alguien la resuelve. */
  retenida: boolean;
  condicion: CondicionAlarma;
  escalada: boolean;
  nivelEscalacion: number | null;
  fechaInicio: Date | null;
  fechaFin: Date | null;
  fechaReconocimiento: Date | null;
  reconocidaPor: string | null;
  nodoId: number | null;
  nodoNombre: string | null;
  areaId: number | null;
  areaNombre: string | null;
  clienteId: number | null;
  clienteNombre: string | null;
}

/** Duración descompuesta, para poder formatearla sin volver a calcular. */
export interface Duracion {
  totalSegundos: number;
  dias: number;
  horas: number;
  minutos: number;
  segundos: number;
}

/**
 * Alarma disparada por un umbral.
 *
 * Concentra las reglas de qué se puede hacer con ella, cuánto lleva abierta y
 * cómo se presenta, que en la versión anterior estaban repartidas en una
 * docena de funciones sueltas del módulo de alarmas.
 */
export class Alarma {
  constructor(private readonly props: AlarmaProps) {}

  get id(): string {
    return this.props.id;
  }

  /** Identificador corto para mostrar en la tabla sin ocupar media pantalla. */
  get idCorto(): string {
    return this.props.id.slice(0, 8);
  }

  get sensorId(): number | null {
    return this.props.sensorId;
  }
  get sensorNombre(): string {
    return this.props.sensorNombre ?? 'Sensor desconocido';
  }
  get esDigital(): boolean {
    return this.props.esDigital;
  }
  get criticidad(): Criticidad {
    return this.props.criticidad;
  }
  get estado(): EstadoAlarma {
    return this.props.estado;
  }
  get mensaje(): string {
    return this.props.mensaje ?? '';
  }
  get observaciones(): string | null {
    return this.props.observaciones;
  }
  get retenida(): boolean {
    return this.props.retenida;
  }
  get condicion(): CondicionAlarma {
    return this.props.condicion;
  }
  get escalada(): boolean {
    return this.props.escalada;
  }
  get nivelEscalacion(): number | null {
    return this.props.nivelEscalacion;
  }
  get fechaInicio(): Date | null {
    return this.props.fechaInicio;
  }
  get fechaFin(): Date | null {
    return this.props.fechaFin;
  }
  get fechaReconocimiento(): Date | null {
    return this.props.fechaReconocimiento;
  }
  get reconocidaPor(): string | null {
    return this.props.reconocidaPor;
  }
  get nodoId(): number | null {
    return this.props.nodoId;
  }
  get nodoNombre(): string | null {
    return this.props.nodoNombre;
  }
  get areaId(): number | null {
    return this.props.areaId;
  }
  get areaNombre(): string | null {
    return this.props.areaNombre;
  }
  get clienteId(): number | null {
    return this.props.clienteId;
  }
  get clienteNombre(): string | null {
    return this.props.clienteNombre;
  }
  get tipoUmbral(): string {
    return this.props.tipoUmbral === 'analogico' ? 'Analógico' : 'Digital';
  }

  get estaAbierta(): boolean {
    return this.props.estado.estaAbierta;
  }

  /** Valor que disparó la alarma, con su unidad. */
  get valorMostrable(): string {
    if (this.props.esDigital) {
      if (this.props.estadoDisparo === null) return '—';
      return this.props.estadoDisparo ? 'ON' : 'OFF';
    }

    const valor = this.props.valorDisparo;
    if (valor === null) return '—';

    const numero = valor.toFixed(1);
    return this.props.sensorUnidad ? `${numero} ${this.props.sensorUnidad}` : numero;
  }

  /**
   * Descripción del umbral que se incumplió, en el mismo formato que la
   * aplicación actual: banda, sólo mínimo, sólo máximo o estado esperado.
   */
  get umbralMostrable(): string {
    const unidad = this.props.sensorUnidad ? ` ${this.props.sensorUnidad}` : '';

    if (this.props.esDigital || this.props.tipoUmbral === 'digital') {
      if (this.props.estadoEsperado === null) return 'Digital';
      return `Esperado ${this.props.estadoEsperado ? 'ON' : 'OFF'}`;
    }

    const { umbralMinimo: min, umbralMaximo: max } = this.props;

    if (min !== null && max !== null)
      return `${min.toFixed(1)}${unidad} – ${max.toFixed(1)}${unidad}`;
    if (min !== null) return `Bajo < ${min.toFixed(1)}${unidad}`;
    if (max !== null) return `Alto > ${max.toFixed(1)}${unidad}`;
    return '—';
  }

  get histeresisMostrable(): string {
    const h = this.props.histeresis;
    if (h === null) return '—';
    return this.props.sensorUnidad ? `${h.toFixed(1)} ${this.props.sensorUnidad}` : h.toFixed(1);
  }

  /** Ubicación legible: «Área · Nodo», omitiendo lo que falte. */
  get ubicacion(): string {
    return [this.props.areaNombre, this.props.nodoNombre].filter(Boolean).join(' · ') || '—';
  }

  /**
   * Explicación de la condición, para el indicador que acompaña al estado.
   * `null` cuando no aplica o no se conoce.
   */
  get textoCondicion(): string | null {
    if (this.props.estado.esResuelta) return 'Cerrada';
    if (this.props.condicion === 'presente') return 'La condición sigue presente';
    if (this.props.condicion === 'normalizada') return 'Volvió a normal · pendiente de cierre';
    return null;
  }

  /**
   * Tiempo transcurrido. Para alarmas cerradas se mide hasta el cierre; para
   * las abiertas, hasta ahora.
   */
  duracion(ahora: Date = new Date()): Duracion | null {
    const inicio = this.props.fechaInicio;
    if (!inicio) return null;

    const fin = this.props.fechaFin ?? ahora;
    const totalSegundos = Math.max(0, Math.floor((fin.getTime() - inicio.getTime()) / 1000));

    return {
      totalSegundos,
      dias: Math.floor(totalSegundos / 86400),
      horas: Math.floor((totalSegundos % 86400) / 3600),
      minutos: Math.floor((totalSegundos % 3600) / 60),
      segundos: totalSegundos % 60,
    };
  }

  /** Duración en texto corto: «2 d 4 h», «15 m», «45 s». */
  duracionMostrable(ahora: Date = new Date()): string {
    const d = this.duracion(ahora);
    if (!d) return '—';

    if (d.dias > 0) return `${d.dias} d ${d.horas} h`;
    if (d.horas > 0) return `${d.horas} h ${d.minutos} m`;
    if (d.minutos > 0) return `${d.minutos} m`;
    return `${d.segundos} s`;
  }

  /**
   * Reconocer exige que la alarma esté activa y tenga retención.
   * Sin retención el servidor rechaza la operación, así que el botón no debe
   * ofrecerse siquiera.
   */
  get puedeReconocerse(): boolean {
    return this.props.estado.esActiva && this.props.retenida;
  }

  /**
   * Resolver exige que la alarma siga abierta, tenga retención y que la
   * condición ya se haya normalizado: cerrar una alarma cuyo sensor sigue
   * fuera de rango sólo la haría reaparecer.
   */
  get puedeResolverse(): boolean {
    return (
      this.props.estado.estaAbierta && this.props.retenida && this.props.condicion === 'normalizada'
    );
  }

  /** Explica al usuario por qué la acción no está disponible. */
  motivoNoReconocible(): string | null {
    if (this.puedeReconocerse) return null;

    if (this.props.estado.esReconocida) return 'Esta alarma ya fue reconocida';
    if (this.props.estado.esResuelta) return 'Esta alarma ya fue resuelta';
    if (!this.props.retenida) return 'Solo se reconocen alarmas con retención activada';
    return 'No se puede reconocer en este momento';
  }

  motivoNoResoluble(): string | null {
    if (this.puedeResolverse) return null;

    if (this.props.estado.esResuelta) return 'Esta alarma ya fue resuelta';
    if (!this.props.retenida) return 'Solo se resuelven alarmas con retención activada';
    if (this.props.condicion === 'presente') {
      return 'No se puede cerrar: la condición sigue presente';
    }
    return 'No se puede resolver en este momento';
  }

  conEstado(estado: EstadoAlarma, cambios: Partial<AlarmaProps> = {}): Alarma {
    return new Alarma({ ...this.props, ...cambios, estado });
  }

  coincideCon(texto: string): boolean {
    const t = texto.trim().toLowerCase();
    if (!t) return true;
    return [
      this.props.sensorNombre,
      this.props.mensaje,
      this.props.nodoNombre,
      this.props.areaNombre,
      this.props.clienteNombre,
    ].some((campo) => campo?.toLowerCase().includes(t));
  }
}
